// FILE: src/pages/main/AdminSidebar.jsx
import React, { useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Shield,
  Server,
  Twitch,
  MonitorPlay,
  Settings2,
  BarChart3,
  Users,
  CreditCard,
  ArrowLeft,
  Tv2,
  Radio,
} from "lucide-react";

/**
 * Админ-сайдбар (монтируется только на /admin/*).
 * Роль тут НЕ проверяем — это делает RequireAdmin.
 */
const AdminSidebar = ({ visible, onClose }) => {
  const navigate = useNavigate();
  const ref = useRef(null);

  const handleReturn = useCallback(() => navigate("/feed"), [navigate]);

  // click-outside / Esc
  useEffect(() => {
    if (!visible) return;
    const onDocClick = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (e.target.closest(".toggle-btn")) return;
      onClose?.();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("pointerdown", onDocClick, true);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("pointerdown", onDocClick, true);
      document.removeEventListener("keydown", onEsc);
    };
  }, [visible, onClose]);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-3 py-2 transition
     ${isActive
       ? "bg-violet-600/20 text-white ring-1 ring-violet-500"
       : "text-slate-300 hover:text-white hover:bg-white/5"}`;

  return (
    <aside
      ref={ref}
      className={`fixed top-0 left-0 z-50 h-full w-72 backdrop-blur-xl
        bg-slate-900/70 border-r border-white/10 shadow-2xl
        transition-transform duration-300
        ${visible ? "translate-x-0" : "-translate-x-full"}`}
      aria-hidden={!visible}
    >
      {/* Header */}
      <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-r from-violet-700/30 via-transparent to-violet-700/30 pointer-events-none" />
      <div className="relative pt-20 pb-4 px-4">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-wider text-slate-400/80">
            Панель администратора
          </div>
          <h2 className="text-xl font-semibold text-white">Навигация</h2>
        </div>

        {/* Menu */}
        <nav className="space-y-2">
          <NavLink to="/admin" end className={linkClass} onClick={() => onClose?.()}>
            <Shield className="h-4 w-4" />
            <span>Администрирование</span>
          </NavLink>

          <NavLink to="/admin/proxy" className={linkClass} onClick={() => onClose?.()}>
            <Server className="h-4 w-4" />
            <span>Управление прокси</span>
          </NavLink>

          <NavLink to="/admin/twitch" className={linkClass} onClick={() => onClose?.()}>
            <Twitch className="h-4 w-4" />
            <span>Администрирование Twitch</span>
          </NavLink>

          <NavLink to="/admin/kick" className={linkClass} onClick={() => onClose?.()}>
            <MonitorPlay className="h-4 w-4" />
            <span>Администрирование Kick</span>
          </NavLink>

          <NavLink to="/admin/wtv" className={linkClass} onClick={() => onClose?.()}>
            <Tv2 className="h-4 w-4" />
            <span>Администрирование W.tv</span>
          </NavLink>

          <NavLink to="/admin/kasada" className={linkClass} onClick={() => onClose?.()}>
            <Settings2 className="h-4 w-4" />
            <span>Настройки Kasada</span>
          </NavLink>

          <NavLink to="/admin/users" className={linkClass} onClick={() => onClose?.()}>
            <Users className="h-4 w-4" />
            <span>Управление пользователями</span>
          </NavLink>

          <NavLink to="/admin/online-users" className={linkClass} onClick={() => onClose?.()}>
            <Radio className="h-4 w-4" />
            <span>Онлайн пользователи</span>
          </NavLink>

          <NavLink to="/admin/transactions" className={linkClass} onClick={() => onClose?.()}>
            <CreditCard className="h-4 w-4" />
            <span>Транзакции</span>
          </NavLink>

          <NavLink to="/admin/tokens" className={linkClass} onClick={() => onClose?.()}>
            <CreditCard className="h-4 w-4" />
            <span>Токены</span>
          </NavLink>

          <NavLink to="/admin/analytics" className={linkClass} onClick={() => onClose?.()}>
            <BarChart3 className="h-4 w-4" />
            <span>Аналитика</span>
          </NavLink>
        </nav>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-white/10">
          <button
            onClick={() => {
              onClose?.();
              handleReturn();
            }}
            className="w-full inline-flex items-center justify-center gap-2
              rounded-xl px-3 py-2
              bg-white/10 hover:bg-white/20 text-white
              border border-white/20 transition"
          >
            <ArrowLeft className="h-4 w-4" />
            Вернуться назад
          </button>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
